import React from 'react';
import { Plugin, Template, TemplateConnector } from '@devexpress/dx-react-core';
import { Table, VirtualTable } from '@devexpress/dx-react-grid-material-ui';
import { Highlighter } from 'app/common/table/Highlighter';
import { TableRow } from '@devexpress/dx-react-grid';
import Utils from '../../util/Utils';

interface WebTableRowProps<T> extends Table.DataRowProps {
    selection: Array<number | string>; // Current selection
    toggleSelection?(selection: { rowIds: Array<number | string>; state?: boolean }): void; // Function to update selection
    tableBodyRows: Array<TableRow>; // Contains the table rows, in the displayed order (when sorting is applied)
    highlighter?: Highlighter<T>;
    highlight?: (object: T) => boolean;
}

/**
 * The DesktopTableRow class renders a TableRow with the following features:
 *   - Desktop like row selection (click, shift+click, ctrl+click)
 *   - Highlight rows that are captured by the provided highlighter
 */
class WebTableRow<T> extends React.PureComponent<WebTableRowProps<T>> {
    handleClick = (event, value) => {
        this.props.toggleSelection({ rowIds: [this.props.tableRow.rowId] });
    };

    render() {
        const row: T = this.props.tableRow.row;

        const cssClasses = [];
        const highlighted = this.props.highlighter?.highlight(row);
        if (highlighted) cssClasses.push(highlighted);
        if (this.props?.highlight?.(row)) {
            cssClasses.push('highlight');
        }
        const selected = this.props.selection.indexOf(this.props.tableRow.rowId) !== -1;
        if (selected) cssClasses.push('selected-row');
        return (
            <VirtualTable.Row
                className={Utils.concat(' ', cssClasses)}
                onClick={this.handleClick}
                row={this.props.row}
                tableRow={this.props.tableRow}>
                {this.props.children}
            </VirtualTable.Row>
        );
    }
}

interface WebBehaviourProps<T> {
    highlighter: Highlighter<T>;
    highlight?: (object: T) => boolean;
}

/**
 * The WebBehaviour plugin overrides the rendering of table row, by adding the following features:
 *   - Highlight rows that are captured by the provided highlighter
 */
export class WebBehaviour<T> extends React.PureComponent<WebBehaviourProps<T>> {
    render() {
        // TODO: Add plugin "dependencies" attribute
        return (
            <Plugin>
                <Template
                    name="tableRow"
                    predicate={(params: Table.RowProps) => params.tableRow.type.description === 'data'}>
                    {(params: Table.DataRowProps) => (
                        <TemplateConnector>
                            {({ selection, tableBodyRows }, { toggleSelection }) => {
                                return (
                                    <WebTableRow
                                        {...params}
                                        tableRow={params.tableRow}
                                        selection={selection}
                                        toggleSelection={toggleSelection}
                                        tableBodyRows={tableBodyRows}
                                        highlighter={this.props.highlighter}
                                        highlight={this.props.highlight}
                                    />
                                );
                            }}
                        </TemplateConnector>
                    )}
                </Template>
            </Plugin>
        );
    }
}
